import Accordion from '@material-ui/core/Accordion';
import AccordionDetails from '@material-ui/core/AccordionDetails';
import AccordionSummary from '@material-ui/core/AccordionSummary';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { ChangeEvent, ReactElement, ReactNode, useRef } from 'react';

import smallAvatarIcon from '~/ui/assets/images/smallAvatar.svg';
import PrivateImage from '~/ui/components/common/PrivateImage';
import ErrorTooltip from '~/ui/pages/ActTeam/reusable/ErrorTooltip';
import { IDatesValidation } from '~/services/api/clientAllocation';
import getTeamMemberDatesErrors from '../../helpers/getTeamMemberDatesErrors';

import styles from './TeamMemberAccordion.module.scss';
import variables from '~/ui/assets/styles/colors.module.scss';

interface IProps {
  panelId: string;
  name: string;
  photo?: string;
  countLabel: string;
  expanded: boolean;
  onToggle: (
    panelId: string,
  ) => (event: ChangeEvent<Record<string, unknown>>, isExpanded: boolean) => void;
  badge?: ReactNode;
  teamMemberId: number;
  datesValidation?: IDatesValidation;
  children: ReactNode;
}

const TeamMemberAccordion = ({
  panelId,
  name,
  photo,
  teamMemberId,
  countLabel,
  expanded,
  onToggle,
  badge,
  datesValidation,
  children,
}: IProps): ReactElement => {
  const detailsRef = useRef<HTMLDivElement>(null);
  const datesErrors = getTeamMemberDatesErrors(teamMemberId, datesValidation);

  return (
    <Accordion
      className={styles.accordion}
      TransitionProps={{
        unmountOnExit: true,
        onEntered: () =>
          detailsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' }),
      }}
      expanded={expanded}
      onChange={onToggle(panelId)}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        aria-controls="panel-content"
        id="panel-header"
      >
        <Grid container>
          <Grid item xs={4} className={styles.accordionSummaryItemName}>
            <div className={styles.photoContainer}>
              <PrivateImage className={styles.image} src={photo || smallAvatarIcon} alt="avatar" />
              {badge}
            </div>
            <Typography>{name}</Typography>
            {datesValidation?.isValid === false && !!datesErrors?.errorDates?.length && (
              <ErrorTooltip items={datesErrors?.errorDates} color={variables.colorOrange} />
            )}
          </Grid>

          <Grid item xs={4} className={styles.accordionSummaryItemCenter}>
            <Typography>{countLabel}</Typography>
          </Grid>

          <Grid item xs={4} className={styles.accordionSummaryItemRight}>
            <Typography>Details</Typography>
          </Grid>
        </Grid>
      </AccordionSummary>

      <AccordionDetails>
        <div ref={detailsRef}>{children}</div>
      </AccordionDetails>
    </Accordion>
  );
};

export default TeamMemberAccordion;
